import { useApplicantAPI } from "@certn/api";
import { rem } from "@certn/cascadia";
import { SectionMessage } from "@certn/cascadia-react";
import { CascadiaJsonFormContext } from "@certn/forms";
import { RenderError } from "@certn/forms/src/components/helpers/RenderError";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { BaseLayoutContent } from "@certn/forms/src/layouts/BaseLayoutContent";
import { hasLabel } from "@certn/forms/src/utils";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms/src/wrappers/withCascadiaLayoutProps";
import { css } from "@emotion/css";
import { rankWith, schemaTypeIs } from "@jsonforms/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useApplicationFormContext } from "../ApplicationFormContext";
import { NonBiometricIdSection } from "../controls/identity-verification/non-biometric-idv/NonBiometricIdSection";
import { NonBiometricIdSummaryControl } from "../controls/identity-verification/non-biometric-idv/NonBiometricIdSummaryControl";
import { BiometricIdSection } from "../controls/one-id/BiometricIdSection";
import { OneIdControlRenderer } from "../controls/one-id/OneIdControl";
import { useHandleChange } from "../utils";
import { OneIdLayoutContext } from "./OneIdLayoutContext";
import { useAuth } from "@certn/auth";

type NonBioOptOutValues = {
  opt_out_value: boolean;
  opt_out_reason: string;
  other_reason: string;
};

function OneIdLayout(props: CascadiaLayoutProps) {
  const handleChange = useHandleChange();
  const { t } = useTranslation();
  const { isSummary } = useContext(CascadiaJsonFormContext);
  const [showNonBiometricIdSection, setShowNonBiometricIdSection] =
    useState(false);
  const [optedOutOfBiometricId, setOptedOutOfBiometricId] = useState(false);
  const [optedOutOfNonBiometricId, setOptedOutOfNonBiometricId] =
    useState(false);
  const { applicantPortalOneIdSectionSupportingNonBio } = useFlags();
  const { caseId } = useApplicationFormContext();
  const { user } = useAuth();

  const fallbackPollTimeout =
    (props.uischema?.options?.fallback_poll_timeout_seconds || 15) * 1000;

  const { uischema, schema, enabled, data } = props;
  const options = uischema.options || {};

  const { data: applicantData, mutate } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/` as "/api/internal/applicant/case/{id}/"
  );
  useEffect(() => {
    const optedOutOfBiometricIdBool =
      (applicantData?.applicant_form_data
        ?.biometric_id_verification_opt_out as boolean) ?? false;
    setOptedOutOfBiometricId(optedOutOfBiometricIdBool);
    const optedOutOfNonBiometricIdBool = (
      applicantData?.applicant_form_data
        .non_biometric_id_verification_opt_out as NonBioOptOutValues
    )?.opt_out_value;
    setOptedOutOfNonBiometricId(optedOutOfNonBiometricIdBool);
  }, [
    applicantData?.applicant_form_data.biometric_id_verification_opt_out,
    applicantData?.applicant_form_data.non_biometric_id_verification_opt_out,
  ]);

  const { data: oneIdAlternativesEligibility } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/one-id-alternative-type-eligibility/` as "/api/internal/applicant/case/${id}/one-id-alternative-type-eligibility/"
  );

  const { data: idvCheck, mutate: mutateIdvCheck } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/idv-check/`
  );

  const idvCheckTypes = idvCheck?.idv_checks;

  const consumer = applicantData?.customer_name === user?.email;

  useEffect(() => {
    const hasCompletedNonBio = applicantData?.applicant_form_data.interac_code;
    if (
      optedOutOfNonBiometricId ||
      (optedOutOfBiometricId &&
        oneIdAlternativesEligibility?.fallback_type ===
          "NON_BIOMETRIC_CANADA") ||
      hasCompletedNonBio
    ) {
      setShowNonBiometricIdSection(true);
    }
  }, [
    applicantData?.applicant_form_data.interac_code,
    oneIdAlternativesEligibility?.fallback_type,
    optedOutOfBiometricId,
    optedOutOfNonBiometricId,
  ]);

  if (isSummary) {
    // We don't render anything in the summary for OneIds.
    if (optedOutOfNonBiometricId) {
      return <NonBiometricIdSummaryControl />;
    }
    return null;
  }

  if (!enabled) {
    return (
      <SectionMessage
        type="danger"
        sectionTitle={t("This section is disabled")}
        icon="info"
      >
        <Trans>
          Please visit the previous section to provide the required information.
        </Trans>
      </SectionMessage>
    );
  }

  if (!hasLabel(uischema)) {
    return <RenderError>Invalid Category</RenderError>;
  }
  return (
    <BaseLayout {...props} className={styles.layout}>
      <OneIdLayoutContext.Provider
        value={{
          label: uischema.label,
          subLabel: options.subLabel,
          showNonBiometricIdSection,
          setShowNonBiometricIdSection,
          handleChange,
          fallbackPollTimeout,
          optedOutOfBiometricId,
          optedOutOfNonBiometricId,
          oneIdAlternativesEligibility,
          mutate,
          idvCheckTypes,
          mutateIdvCheck,
          onFallbackTriggered: () => {},
          consumer,
        }}
      >
        {applicantPortalOneIdSectionSupportingNonBio ? (
          showNonBiometricIdSection ? (
            <NonBiometricIdSection
              schema={schema}
              data={applicantData}
              fulfillWithDemoResponses={
                applicantData?.fulfill_with_demo_responses ?? {
                  only_fulfill_with_demo_responses: false,
                  check_types_allowing_non_demo_responses: [],
                }
              }
            />
          ) : (
            <BiometricIdSection
              data={data["one_id_session_id"]}
              uischema={uischema}
              fulfillWithDemoResponses={
                applicantData?.fulfill_with_demo_responses ?? {
                  only_fulfill_with_demo_responses: false,
                  check_types_allowing_non_demo_responses: [],
                }
              }
              consumer={consumer}
            />
          )
        ) : (
          <BaseLayoutContent
            renderers={[
              {
                tester: rankWith(2, schemaTypeIs("string")),
                renderer: OneIdControlRenderer,
              },
              // hack to not render biometric opt out elements with old OneID layout
              {
                tester: rankWith(1, () => true),
                renderer: () => null,
              },
            ]}
          />
        )}
      </OneIdLayoutContext.Provider>
    </BaseLayout>
  );
}

const styles = {
  layout: css({
    display: "flex",
    flexDirection: "column",
    gap: rem("space-4"),
  }),
};

OneIdLayout.displayName = "OneIdLayout";

export const OneIdLayoutRenderer = withCascadiaLayoutProps(OneIdLayout);
